// src/components/CreateAssessmentPopup.js
import React, { useState, useEffect } from 'react';
import './CreateAssessmentPopup.css';
import apiService from '../services/api';
import Snackbar from './Snackbar';

const CreateAssessmentPopup = ({
  open,
  onClose,
  onSave,
  departmentsData,
  rolesData,
  categoriesMetadata,
  isMetadataLoading
}) => {
  const [formData, setFormData] = useState({
    department: '',
    department_id: '',
    role: '',
    created_by: sessionStorage.getItem('user_emailId'),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localCategoriesMetadata, setLocalCategoriesMetadata] = useState(categoriesMetadata);

  const [categories, setCategories] = useState([
    { id: null, name: '', questions: [{ question: '', description: '', weightage: '' }] }
  ]);
  const [snackbar, setSnackbar] = useState({ message: '', type: 'success', isOpen: false });
  const showSnackbar = (message, type = 'success') => {
    setSnackbar({ message, type, isOpen: true });
  };
  const [deptSearch, setDeptSearch] = useState('');
  const [roleSearch, setRoleSearch] = useState('');
  const [deptDropdownOpen, setDeptDropdownOpen] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [existingAssessments, setExistingAssessments] = useState([]);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [validationError, setValidationError] = useState('');
  const categoryOptions = localCategoriesMetadata?.data?.map(cat => ({
    id: cat.CategoryID,
    name: cat.CategoryName,
    description: cat.Description
  })) || [];

  const addCategory = () => {
    setCategories([...categories, { id: null, name: '', description: '', questions: [{ question: '', weightage: '' }] }]);
  };

  const removeCategory = (index) => {
    if (categories.length === 1) return;
    setCategories(categories.filter((_, i) => i !== index));
  };

  const updateCategoryName = (index, value) => {
    const selected = categoryOptions.find(opt => opt.name === value);
    const updated = [...categories];
    updated[index].name = value;
    updated[index].id = selected ? selected.id : null;
    updated[index].description = selected ? selected.description : '';
    setCategories(updated);
  };

  const addQuestion = (catIndex) => {
    const updated = [...categories];
    updated[catIndex].questions.push({ question: '', description: '', weightage: '' });
    setCategories(updated);
  };

  const removeQuestion = (catIndex, qIndex) => {
    const updated = [...categories];
    updated[catIndex].questions = updated[catIndex].questions.filter((_, i) => i !== qIndex);
    setCategories(updated);
  };

  const updateQuestion = (catIndex, qIndex, field, value) => {
    const updated = [...categories];
    updated[catIndex].questions[qIndex][field] = value;
    setCategories(updated);
  };

  const validateForm = () => {
    // 1. Check if any category is not selected
    const unselectedCategory = categories.find(cat => !cat.name || !cat.id);
    if (unselectedCategory) {
      setValidationError('All categories must be selected from the dropdown.');
      showSnackbar('Please select a category for each section.', 'error');
      return false;
    }

    // 2. Check for duplicate categories
    const selectedCategoryNames = categories.map(cat => cat.name);
    const hasDuplicateCategory = selectedCategoryNames.some(
      (name, idx) => selectedCategoryNames.indexOf(name) !== idx
    );
    if (hasDuplicateCategory) {
      setValidationError('Each category can only be used once.');
      showSnackbar('You cannot use the same category more than once.', 'error');
      return false;
    }

    // 3. Check if any question is empty
    for (const cat of categories) {
      for (const q of cat.questions) {
        if (!q.question?.trim()) {
          setValidationError('All questions are required.');
          showSnackbar('Please fill in all question fields.', 'error');
          return false;
        }
        if (!q.weightage || isNaN(q.weightage) || q.weightage <= 0) {
          setValidationError('Weightage must be a positive number.');
          showSnackbar('Please enter a valid weightage (greater than 0) for all questions.', 'error');
          return false;
        }
      }
    }

    setValidationError('');
    return true;
  };
  const calculateTotalWeightage = () => {
    return categories.reduce((total, cat) => {
      return total + cat.questions.reduce((sum, q) => sum + (parseInt(q.weightage) || 0), 0);
    }, 0);
  };

  const fetchExistingAssessments = async () => {
    try {
      setCheckingDuplicate(true);
      const userRole = (sessionStorage.getItem('access_role') || '').toLowerCase();
      const isAdmin = ['admin', 'administrator', 'superadmin'].includes(userRole);
      const managerEmail = sessionStorage.getItem('user_emailId') || '';

      let endpoint;
      if (isAdmin) {
        endpoint = 'manager-role-questions?is_admin=true';
      } else {
        endpoint = `manager-role-questions?manager_email=${encodeURIComponent(managerEmail)}`;
      }

      const response = await apiService.fetchData(endpoint);

      if (response.status === 'success') {
        const combos = new Set();
        response.data.forEach(dept => {
          dept.roles.forEach(roleObj => {
            const key = `${dept.department_name.trim()}|${roleObj.role.trim()}`.toLowerCase();
            combos.add(key);
          });
        });
        setExistingAssessments(Array.from(combos));
      }
    } catch (err) {
      console.warn('Could not fetch existing assessments for duplicate check:', err);
      // Don't block creation if this fails — just skip duplicate check
      setExistingAssessments([]);
    } finally {
      setCheckingDuplicate(false);
    }
  };

  const totalWeightage = calculateTotalWeightage();
  const isWeightageValid = totalWeightage === 100;

  useEffect(() => {
    if (!departmentsData?.data) {
      setAvailableDepartments([]);
      return;
    }

    const userRole = (sessionStorage.getItem('access_role') || '').toLowerCase().trim();
    const userDepartment = sessionStorage.getItem('user_department') || '';

    const isAdmin = ['admin', 'administrator', 'superadmin'].includes(userRole);

    let filtered = [];

    if (isAdmin) {
      filtered = departmentsData.data;
    } else if (userDepartment) {
      const matched = departmentsData.data.find(
        d => d.DepartmentName?.toLowerCase() === userDepartment.toLowerCase()
      );
      if (matched) filtered = [matched];
    }


    setAvailableDepartments(filtered);
  }, [departmentsData]);

  useEffect(() => {
    const userRole = (sessionStorage.getItem('access_role') || '').toLowerCase().trim();
    const isAdmin = ['admin', 'administrator', 'superadmin'].includes(userRole);

    if (!isAdmin && availableDepartments.length === 1 && !formData.department) {
      const department = availableDepartments[0];
      setFormData(prev => ({
        ...prev,
        department: department.DepartmentName,
        department_id: department.DepartmentID
      }));
    }
  }, [availableDepartments, formData.department]);

  // Filter roles based on selected department
  // Fixed: Filter roles based on DepartmentID, not DepartmentName
  // Filter roles based on selected department
  // NEW: Fetch roles using correct department_id from formData
  useEffect(() => {
    if (!formData.department_id) {
      setAvailableRoles([]);
      return;
    }

    const fetchRoles = async () => {
      try {
        const endpoint = `/metadata?type=roles&department_id=${formData.department_id}`;
        console.log('Fetching roles for department_id:', formData.department_id);

        const response = await apiService.fetchData(endpoint);

        if (response?.status === 'success' && Array.isArray(response.data)) {
          setAvailableRoles(response.data);
        } else {
          setAvailableRoles([]);
        }
      } catch (err) {
        console.error('Failed to load roles for department:', err);
        setAvailableRoles([]);
      }
    };

    fetchRoles();
  }, [formData.department_id]);

  // Fetch categories when role is selected
  useEffect(() => {
    if (!formData.department_id || !formData.role) {
      return;
    }

    const fetchCategoriesForRole = async () => {
      try {
        // Find the selected role's ID
        const selectedRole = availableRoles.find(role => role.RoleName === formData.role);
        if (!selectedRole) return;

        const endpoint = `/metadata?type=categories&department_id=${formData.department_id}&role_id=${selectedRole.RoleID}`;
        console.log('Fetching categories for department_id and role_id:', formData.department_id, selectedRole.RoleID);

        const response = await apiService.fetchData(endpoint);

        if (response?.status === 'success' && Array.isArray(response.data)) {
          // Update the categories metadata that's used by the component
          setLocalCategoriesMetadata({ data: response.data });
        }
      } catch (err) {
        console.error('Failed to load categories for role:', err);
      }
    };

    fetchCategoriesForRole();
  }, [formData.department_id, formData.role, availableRoles]);

  // Sync local categories metadata with prop changes
  useEffect(() => {
    setLocalCategoriesMetadata(categoriesMetadata);
  }, [categoriesMetadata]);

  // Add this near the top with other useEffect
  useEffect(() => {
    if (!open) return;

    // Reset form when popup opens
    setFormData({
      department: '',
      department_id: '',
      role: '',
      created_by: sessionStorage.getItem('user_emailId'),
    });
    setCategories([
      { id: null, name: '', description: '', questions: [{ question: '', description: '', weightage: '' }] }
    ]);

    // Fetch existing assessments to check for duplicates
    fetchExistingAssessments();
  }, [open]);

  useEffect(() => {
    const handleClickOutside = () => {
      setDeptDropdownOpen(false);
      setRoleDropdownOpen(false);
      setDeptSearch('');
      setRoleSearch('');
    };

    if (deptDropdownOpen || roleDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => document.removeEventListener('click', handleClickOutside);
  }, [deptDropdownOpen, roleDropdownOpen]);

  const handleSubmit = async () => {
    if (!formData.department || !formData.role) {
      showSnackbar('Department and Role are required.', 'error');
      return;
    }

    // Run full validation
    if (!validateForm()) {
      return; // Stop if validation fails
    }

    // IMPROVED DUPLICATE CHECK
    const normalize = (str) => str?.trim().toLowerCase();

    const selectedDept = normalize(formData.department);
    const selectedRole = normalize(formData.role);

    const isDuplicate = existingAssessments.some(existing => {
      const [existingDept, existingRole] = existing.split('|');
      return normalize(existingDept) === selectedDept &&
        normalize(existingRole) === selectedRole;
    });

    if (isDuplicate) {
      showSnackbar(
        `Assessment already exists for role "${formData.role}" in department "${formData.department}". ` +
        `You cannot create duplicate assessments. Please edit the existing one.`,
        'error'
      );
      return;
    }

    if (!isWeightageValid) {
      showSnackbar('Total weightage must be exactly 100%.', 'error');
      return;
    }

    const payload = {
      department: formData.department.trim(),
      role: formData.role.trim(),
      created_by: formData.created_by,
      questions: categories.flatMap(cat =>
        cat.questions.map(q => ({
          CategoryID: cat.id,
          Question: q.question.trim(),
          Description: q.description?.trim() || '',
          Weightage: parseInt(q.weightage)
        }))
      )
    };

    const loggedInEmail = sessionStorage.getItem('user_emailId');
    const endpoint = `manager-role-questions?manager_email=${encodeURIComponent(loggedInEmail)}`;

    setIsSubmitting(true);

    try {
      await apiService.postData(endpoint, payload);
      showSnackbar('Assessment created successfully!', 'success');
      onSave();
      onClose();
    } catch (err) {
      console.error('POST error:', err);
      showSnackbar(err.message || 'Failed to create assessment.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  if (categoryOptions.length === 0) {
    return (
      <div className="ca-overlay">
        <div className="ca-modal">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            Loading data...
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="ca-overlay" onClick={onClose}>
        <div className="ca-modal" onClick={e => e.stopPropagation()}>
          {/* HEADER */}
          <div className="ca-header">
            <h2 className="ca-title">Create New Assessment</h2>
            <button className="ca-close-btn" onClick={onClose}>X</button>
          </div>

          {/* BODY */}
          <div className="ca-body">
            {/* BASIC INFO */}
            <div className="ca-section">
              <h3 className="ca-section-title">Basic Information</h3>
              <div className="ca-form-row">


                <div className="ca-form-group">
                  <label className="ca-label">Department <span className="ca-required">*</span></label>
                  <div style={{ position: 'relative' }}>
                    <div
                      onClick={() => setDeptDropdownOpen(!deptDropdownOpen)}
                      className="ca-select"
                      style={{
                        cursor: 'pointer',
                        backgroundColor: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <span style={{ color: formData.department ? '#1f2937' : '#9ca3af' }}>
                        {formData.department || 'Select Department'}
                      </span>

                    </div>

                    {deptDropdownOpen && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        marginTop: '4px',
                        maxHeight: '240px',
                        overflow: 'hidden',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                        zIndex: 1000
                      }}>
                        <input
                          type="text"
                          placeholder="Search department..."
                          value={deptSearch}
                          onChange={(e) => setDeptSearch(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderBottom: '1px solid #e5e7eb',
                            outline: 'none',
                            fontSize: '14px'
                          }}
                          autoFocus
                        />
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {availableDepartments
                            .filter(d => d.DepartmentName?.toLowerCase().includes(deptSearch.toLowerCase()))
                            .sort((a, b) => a.DepartmentName.localeCompare(b.DepartmentName))
                            .length === 0 ? (
                            <div style={{
                              padding: '16px 12px',
                              color: '#6b7280',
                              fontStyle: 'italic',
                              textAlign: 'center'
                            }}>
                              {deptSearch ? 'No departments found' : 'Start typing to search...'}
                            </div>
                          ) : (
                            availableDepartments
                              .filter(d => d.DepartmentName?.toLowerCase().includes(deptSearch.toLowerCase()))
                              .sort((a, b) => a.DepartmentName.localeCompare(b.DepartmentName))
                              .map(dept => (
                                <div
                                  key={dept.DepartmentID}
                                  onClick={() => {
                                    setFormData(prev => ({ ...prev, department: dept.DepartmentName, department_id: dept.DepartmentID, role: '' }));
                                    setDeptDropdownOpen(false);
                                    setAvailableRoles([]);
                                    setRoleDropdownOpen(false);
                                    setDeptSearch('');
                                  }}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    backgroundColor: formData.department === dept.DepartmentName ? '#eff6ff' : 'transparent'
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = formData.department === dept.DepartmentName ? '#eff6ff' : 'transparent'}
                                >
                                  {dept.DepartmentName}
                                </div>
                              ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="ca-form-group">
                  <label className="ca-label">Role <span className="ca-required">*</span></label>
                  <div style={{ position: 'relative' }}>
                    <div
                      onClick={() => formData.department && setRoleDropdownOpen(!roleDropdownOpen)}
                      className="ca-select"
                      style={{
                        cursor: formData.department ? 'pointer' : 'not-allowed',
                        backgroundColor: formData.department ? 'white' : '#f9fafb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        opacity: formData.department ? 1 : 0.6
                      }}
                    >
                      <span style={{ color: formData.role ? '#1f2937' : '#9ca3af' }}>
                        {formData.role || (formData.department ? 'Select Role' : 'Select Department First')}
                      </span>
                    </div>

                    {roleDropdownOpen && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        marginTop: '4px',
                        maxHeight: '240px',
                        overflow: 'hidden',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                        zIndex: 1000
                      }}>
                        <input
                          type="text"
                          placeholder="Search role..."
                          value={roleSearch}
                          onChange={(e) => setRoleSearch(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderBottom: '1px solid #e5e7eb',
                            outline: 'none',
                            fontSize: '14px'
                          }}
                          autoFocus
                        />
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {availableRoles
                            ?.filter(r => r.RoleName?.toLowerCase().includes(roleSearch.toLowerCase()))
                            .sort((a, b) => a.RoleName.localeCompare(b.RoleName))
                            .length === 0 ? (
                            <div style={{
                              padding: '16px 12px',
                              color: '#6b7280',
                              fontStyle: 'italic',
                              textAlign: 'center'
                            }}>
                              {roleSearch ? 'No roles found' : availableRoles?.length > 0 ? 'Start typing to search...' : 'No roles available for selected department'}
                            </div>
                          ) : (
                            availableRoles
                              .filter(r => r.RoleName?.toLowerCase().includes(roleSearch.toLowerCase()))
                              .sort((a, b) => a.RoleName.localeCompare(b.RoleName))
                              .map(role => (
                                <div
                                  key={role.RoleID}
                                  onClick={() => {
                                    setFormData(prev => ({ ...prev, role: role.RoleName }));
                                    setRoleDropdownOpen(false);
                                    setRoleSearch('');
                                  }}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    backgroundColor: formData.role === role.RoleName ? '#eff6ff' : 'transparent'
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = formData.role === role.RoleName ? '#eff6ff' : 'transparent'}
                                >
                                  {role.RoleName}
                                </div>
                              ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* CATEGORIES */}
            <div className="ca-section">
              <div className="ca-section-title">
                Assessment Categories
                <span className={`ca-weightage-indicator ${isWeightageValid ? 'valid' : ''}`}>
                  Total Weightage: {totalWeightage}/100
                </span>
              </div>

              <div className="ca-categories">
                {categories.map((cat, catIdx) => (
                  <div key={catIdx} className="ca-category-card">
                    <div className="ca-category-header">
                      <div className="ca-category-select-wrapper">
                        <div className="ca-form-group">
                          <label className="ca-label" title={cat.description}>Category {catIdx + 1} <span className="ca-required">*</span></label>

                          <select
                            value={cat.name}
                            onChange={e => updateCategoryName(catIdx, e.target.value)}
                            className="ca-select"
                            title={cat.description || 'Select a category'}
                          >
                            <option value="">Select a category</option>
                            {categoryOptions
                              .filter(opt =>
                                // Exclude categories already used in other sections
                                !categories.some((c, i) => i !== catIdx && c.name === opt.name)
                              )
                              .map(opt => (
                                <option key={opt.id} value={opt.name} title={opt.description}>
                                  {opt.name}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                      {categories.length > 1 && (
                        <button
                          className="ca-delete-category-btn"
                          onClick={() => removeCategory(catIdx)}
                          title="Remove Category"
                        >
                          X
                        </button>
                      )}
                    </div>

                    <div className="ca-questions">
                      {cat.questions.map((q, qIdx) => (
                        <div key={qIdx} className="ca-question-row">
                          <div className="ca-question-input-wrapper">
                            <label className="ca-label">Question {qIdx + 1} <span className="ca-required">*</span></label>
                            <textarea
                              value={q.question}
                              onChange={e => updateQuestion(catIdx, qIdx, 'question', e.target.value)}
                              placeholder="Enter assessment question..."
                              className="ca-textarea"
                              rows={2}
                            />
                          </div>
                          {/* Description input below question input */}
                          <div className="ca-description-input-wrapper" style={{ marginTop: '8px' }}>
                            <label className="ca-label">Description <span style={{ color: '#9ca3af', fontWeight: 'normal' }}>(optional)</span></label>
                            <textarea
                              value={q.description || ''}
                              onChange={e => updateQuestion(catIdx, qIdx, 'description', e.target.value)}
                              placeholder="Enter question description..."
                              className="ca-textarea"
                              rows={2}
                            />
                          </div>
                          <div className="ca-weightage-input-wrapper">
                            <label className="ca-label">Weightage <span className="ca-required">*</span></label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={q.weightage}
                              onChange={e => updateQuestion(catIdx, qIdx, 'weightage', e.target.value)}
                              placeholder="0-100"
                              className="ca-input"
                            />
                          </div>
                          {cat.questions.length > 1 && (
                            <button
                              className="ca-delete-question-btn"
                              onClick={() => removeQuestion(catIdx, qIdx)}
                              title="Remove Question"
                            >
                              X
                            </button>
                          )}
                        </div>
                      ))}

                      <button
                        className="ca-add-btn"
                        onClick={() => addQuestion(catIdx)}
                      >
                        + Add Question
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button className="ca-add-btn ca-add-category-btn" onClick={addCategory}>
                + Add Category
              </button>
            </div>
          </div>

          {/* FOOTER */}
          <div className="ca-footer">
            <div className="ca-footer-left">
              {!isWeightageValid && (
                <span className="ca-warning-text">Warning: Total weightage must be 100%</span>
              )}
            </div>
            <div className="ca-footer-actions">
              <button className="ca-btn ca-btn-cancel" onClick={onClose}>Cancel</button>

              <button
                className="ca-btn ca-btn-submit"
                onClick={handleSubmit}
                disabled={!isWeightageValid || !formData.department || !formData.role || isSubmitting}
              >
                {isSubmitting ? 'Creating assessment...' : 'Create Assessment'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {snackbar.isOpen && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          duration={3000}
          onClose={() => setSnackbar(prev => ({ ...prev, isOpen: false }))}
        />
      )}
    </>
  );
};

export default CreateAssessmentPopup;